import React, { useEffect, useState, useCallback } from "react";
import { api, formatApiError } from "../lib/api";
import { inr, prettyDate } from "../lib/format";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { Card } from "../components/ui/card";
import { ArrowUpRight, ArrowDownRight, Lock, AlertTriangle, FileText, Receipt, Trash2, Pencil, Paperclip } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { usePayrollPending } from "../context/PayrollPendingContext";
import { AttachmentManager } from "../components/AttachmentManager";

const ENTRY_BADGE = {
  cs: { label: "CS", color: "bg-cashin-bg text-cashin" },
  cv: { label: "CV", color: "bg-cashout-bg text-cashout" },
  sa: { label: "SA", color: "bg-amber-50 text-warn" },
  bill: { label: "Bill·CV", color: "bg-paper-alt text-ink" },
  cr: { label: "CR", color: "bg-cashin-bg text-cashin" },
};
const VOUCHER_TYPES = ["cv", "sa", "bill"];

export default function CashBook() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const payrollPending = usePayrollPending();
  const [today, setToday] = useState(null);
  const [book, setBook] = useState(null);
  const [summary, setSummary] = useState(null);
  const [categories, setCategories] = useState([]);
  const [persons, setPersons] = useState([]);
  const [openOB, setOpenOB] = useState(false);
  const [ob, setOB] = useState({ amount: "", remarks: "" });
  const [openCS, setOpenCS] = useState(false);
  const [cs, setCS] = useState({ amount: "", category_id: "", particulars: "", remarks: "" });
  const [openCV, setOpenCV] = useState(false);
  const [cv, setCV] = useState({ amount: "", payment_type: "direct_expense", person_id: "", category_id: "", particulars: "", remarks: "" });
  const [submitting, setSubmitting] = useState(false);
  const [attach, setAttach] = useState({ open: false, txnId: null, docNo: null });

  const load = useCallback(async () => {
    try {
      const [t, s, cats, pers] = await Promise.all([
        api.get("/opening-balance/today"),
        api.get("/dashboard/summary"),
        api.get("/categories"),
        api.get("/persons"),
      ]);
      setToday(t.data);
      setSummary(s.data);
      setOB({ amount: String(t.data.suggested_amount ?? ""), remarks: "" });
      const { data: b } = await api.get(`/cashbook/${t.data.date}`);
      setBook(b);
      setCategories(cats.data.filter(c => c.active));
      setPersons(pers.data.filter(p => p.active));
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const submitOB = async (e) => {
    e.preventDefault(); setSubmitting(true);
    try {
      await api.post("/opening-balance", { amount: parseFloat(ob.amount || "0"), remarks: ob.remarks || null });
      toast.success("Opening balance recorded"); setOpenOB(false); await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
    finally { setSubmitting(false); }
  };

  const submitCS = async (e) => {
    e.preventDefault(); setSubmitting(true);
    try {
      const r = await api.post("/cs", { ...cs, amount: parseFloat(cs.amount), category_id: cs.category_id || null });
      toast.success(`Recorded ${r.data.doc_no}`); setOpenCS(false);
      setCS({ amount: "", category_id: "", particulars: "", remarks: "" }); await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
    finally { setSubmitting(false); }
  };

  const submitCV = async (e) => {
    e.preventDefault(); setSubmitting(true);
    try {
      const body = { amount: parseFloat(cv.amount), payment_type: "direct_expense",
        category_id: cv.category_id || null, person_id: cv.person_id || null,
        particulars: cv.particulars, remarks: cv.remarks || null };
      const r = await api.post("/cv", body);
      toast.success(`Recorded ${r.data.doc_no}`); setOpenCV(false);
      setCV({ amount: "", payment_type: "direct_expense", person_id: "", category_id: "", particulars: "", remarks: "" });
      await load();
      setAttach({ open: true, txnId: r.data.id, docNo: r.data.doc_no });
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
    finally { setSubmitting(false); }
  };

  const deleteTxn = async (id, label) => {
    const remark = window.prompt(`Delete ${label}? Enter remark for audit log:`);
    if (!remark) return;
    try {
      await api.delete(`/transactions/${id}`, { data: { remark } });
      toast.success("Entry deleted"); await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
  };

  const adjustOpening = async () => {
    const amt = window.prompt(`Adjust today's opening (currently ₹${today?.amount}). Enter new amount:`);
    if (!amt) return;
    const remark = window.prompt("Reason for adjustment (required):");
    if (!remark) return;
    try {
      await api.post("/opening-balance/adjust", { amount: parseFloat(amt), remarks: remark });
      toast.success("Opening adjusted"); await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
  };

  const sendClosingEmail = async () => {
    if (!window.confirm("Send today's closing summary email?")) return;
    try {
      const { data } = await api.post("/summary/send-email", {});
      toast.success(`Sent to ${data.recipients?.join(", ") || "recipient"}`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
  };

  if (!today || !book || !summary) return <div className="font-mono text-sm text-muted">Loading…</div>;
  const lowCash = summary.closing_cash < 500;

  return (
    <div className="space-y-8">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="label-eyebrow">Today&apos;s Ledger</div>
          <h1 className="font-heading text-4xl font-bold mt-1" data-testid="cashbook-date">{prettyDate(today.date)}</h1>
        </div>
        {today.recorded && (
          <div className="flex gap-3 flex-wrap">
            {isAdmin && (
              <>
                <Button variant="outline" onClick={adjustOpening} data-testid="adjust-opening-btn">
                  <Pencil size={14} className="mr-1.5" /> Adjust Opening
                </Button>
                <Button variant="outline" onClick={sendClosingEmail} data-testid="send-closing-email-btn">
                  Send Closing Email
                </Button>
              </>
            )}
            <Button variant="outline" onClick={() => setOpenCS(true)} data-testid="add-cs-btn"
              className="border-cashin text-cashin hover:bg-cashin-bg hover:text-cashin">
              <ArrowUpRight size={16} className="mr-1.5" /> Cash Slip / CS
            </Button>
            <Button onClick={() => setOpenCV(true)} data-testid="add-cv-btn"
              className="bg-cashout hover:bg-cashout/90 text-white">
              <ArrowDownRight size={16} className="mr-1.5" /> Cash Voucher / CV
            </Button>
          </div>
        )}
      </div>

      {!today.recorded ? (
        <Card className="p-8 border-2 border-dashed border-warn/40 bg-paper-card" data-testid="opening-balance-required">
          <div className="flex items-start gap-4">
            <div className="h-12 w-12 rounded-md bg-warn/10 text-warn flex items-center justify-center"><Lock size={22} /></div>
            <div className="flex-1">
              <div className="label-eyebrow text-warn">Action Required</div>
              <h3 className="font-heading text-2xl font-semibold mt-1">Record today&apos;s opening balance</h3>
              <p className="text-sm text-muted mt-1.5 max-w-xl">
                No transactions can be posted until today&apos;s opening is recorded. Suggested (previous closing):{" "}
                <span className="font-mono font-medium text-ink">{inr(today.suggested_amount)}</span>
              </p>
              <Button onClick={() => setOpenOB(true)} data-testid="record-opening-balance-btn" className="mt-4 bg-ink hover:bg-ink-soft text-white">
                Record Opening Balance
              </Button>
            </div>
          </div>
        </Card>
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <StatCard label="Opening Cash" v={summary.opening_cash} testid="card-opening" />
            <StatCard label="Today&apos;s CS" v={summary.cs_total} accent="in" testid="card-cs" />
            <StatCard label="Direct Expense" v={summary.direct_expense} accent="out" testid="card-direct" />
            <StatCard label="Suspense Advance" v={summary.suspense_advance} warn testid="card-sus-adv" />
            <StatCard label="Suspense Returned" v={summary.suspense_returned} accent="in" testid="card-sus-ret" />
            <StatCard label="Bill Settled" v={summary.bill_settled} testid="card-bill" />
            <StatCard label="Pending Suspense" v={summary.pending_suspense} warn testid="card-pending" />
            <StatCard label="Closing Cash" v={summary.closing_cash} strong warnLow={lowCash} testid="card-closing" />
            {/* A small addition, not a second dashboard — the top-bar indicator stays the primary call to
                action for this (see PayrollPendingIndicator), this card is informational only. */}
            {isAdmin && payrollPending?.configured && (
              <Card className="p-4 border border-line bg-paper-card overflow-hidden" data-testid="card-payroll-pending">
                <div className="label-eyebrow">Pending Payroll Vouchers</div>
                <div className={`mt-1.5 font-mono tabular-nums whitespace-nowrap text-sm sm:text-base lg:text-lg font-semibold ${payrollPending.count > 0 ? "text-warn" : "text-ink"}`}>
                  {payrollPending.count}
                </div>
              </Card>
            )}
          </div>
          <div className="flex gap-3 text-xs font-mono text-muted">
            <span>CS today: {summary.cs_count}</span><span>·</span><span>CV today: {summary.cv_count}</span><span>·</span><span>SA today: {summary.sa_count}</span>
          </div>
        </>
      )}

      <Card className="overflow-hidden border border-line">
        <div className="px-6 py-4 border-b border-line"><div className="label-eyebrow">Today&apos;s Entries</div></div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-paper-alt border-b border-line">
                <th className="text-left px-3 py-3 label-eyebrow">#</th>
                <th className="text-left px-3 py-3 label-eyebrow">Time</th>
                <th className="text-left px-3 py-3 label-eyebrow">Type</th>
                <th className="text-left px-3 py-3 label-eyebrow">Doc</th>
                <th className="text-left px-3 py-3 label-eyebrow">Person</th>
                <th className="text-left px-3 py-3 label-eyebrow">Particulars</th>
                <th className="text-right px-3 py-3 label-eyebrow">Cash In</th>
                <th className="text-right px-3 py-3 label-eyebrow">Cash Out</th>
                <th className="text-right px-3 py-3 label-eyebrow">Balance</th>
              </tr>
            </thead>
            <tbody data-testid="cashbook-rows">
              <tr className="border-b border-line bg-paper-alt/40">
                <td colSpan={6} className="px-3 py-3 italic text-muted">Opening Balance</td>
                <td></td><td></td><td className="px-3 py-3 text-right font-mono font-semibold">{inr(book.opening_balance)}</td>
              </tr>
              {book.rows.map((r, i) => {
                const b = ENTRY_BADGE[r.entry_type] || { label: r.entry_type, color: "bg-paper-alt" };
                return (
                  <tr key={r.id} className="border-b border-line hover:bg-paper/60">
                    <td className="px-3 py-3 font-mono text-muted">{i + 1}</td>
                    <td className="px-3 py-3 font-mono text-xs text-muted">{(r.created_at || "").slice(11, 16)}</td>
                    <td className="px-3 py-3">
                      <span className={`text-xs px-2 py-0.5 rounded ${b.color}`}>{b.label}</span>
                      {r.source_system && (
                        <span className="ml-1.5 text-xs px-2 py-0.5 rounded bg-paper-alt text-muted" title={r.source_ref || "Payroll"}>
                          Payroll
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-3 font-mono text-xs">{r.doc_no || "—"}</td>
                    <td className="px-3 py-3 text-muted">{r.person_name || "—"}</td>
                    <td className="px-3 py-3">{r.particulars}<div className="text-xs text-muted">{r.category_name || ""}</div></td>
                    <td className="px-3 py-3 text-right font-mono text-cashin">{r.cash_in ? inr(r.cash_in) : ""}</td>
                    <td className="px-3 py-3 text-right font-mono text-cashout">{r.cash_out ? inr(r.cash_out) : ""}</td>
                    <td className="px-3 py-3 text-right font-mono font-medium">
                      {inr(r.running_balance)}
                      {VOUCHER_TYPES.includes(r.entry_type) && (
                        <button onClick={() => setAttach({ open: true, txnId: r.id, docNo: r.doc_no })}
                          data-testid={`attach-${r.id}`}
                          className={`ml-2 align-middle ${r.attachment_count > 0 ? "text-ink" : "text-muted hover:text-ink"}`}
                          title={r.attachment_count > 0 ? `${r.attachment_count} photo(s)` : "Attach photos"}>
                          <Paperclip size={12} className="inline" />
                          {r.attachment_count > 0 && <span className="text-[10px] ml-0.5">{r.attachment_count}</span>}
                        </button>
                      )}
                      {isAdmin && (
                        <button onClick={() => deleteTxn(r.id, r.doc_no || r.entry_label)}
                          data-testid={`del-${r.id}`}
                          className="ml-2 text-muted hover:text-cashout align-middle">
                          <Trash2 size={12} />
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
              {book.rows.length === 0 && (<tr><td colSpan={9} className="px-4 py-12 text-center text-muted text-sm">No entries yet today.</td></tr>)}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Opening Balance */}
      <Dialog open={openOB} onOpenChange={setOpenOB}>
        <DialogContent>
          <DialogHeader><DialogTitle>Record Opening Balance</DialogTitle>
            <DialogDescription>Once recorded, today&apos;s opening is locked.</DialogDescription></DialogHeader>
          <form onSubmit={submitOB} className="space-y-4">
            <div><Label className="label-eyebrow">Date</Label><div className="mt-1.5 font-mono">{today.date}</div></div>
            <div><Label className="label-eyebrow">Amount (₹)</Label>
              <Input type="number" min="0" step="0.01" value={ob.amount} onChange={(e) => setOB({ ...ob, amount: e.target.value })}
                required data-testid="opening-balance-input" className="mt-1.5 font-mono" /></div>
            <div><Label className="label-eyebrow">Remarks (required if adjusting)</Label>
              <Input value={ob.remarks} onChange={(e) => setOB({ ...ob, remarks: e.target.value })}
                placeholder="e.g. counted cash on hand"  className="mt-1.5" /></div>
            <DialogFooter><Button type="button" variant="outline" onClick={() => setOpenOB(false)}>Cancel</Button>
              <Button type="submit" disabled={submitting} data-testid="opening-balance-submit-btn" className="bg-ink text-white">Record</Button></DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Cash Slip */}
      <Dialog open={openCS} onOpenChange={setOpenCS}>
        <DialogContent>
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Receipt size={18} /> Cash Slip / CS</DialogTitle>
            <DialogDescription>Cash received into the cash box.</DialogDescription></DialogHeader>
          <form onSubmit={submitCS} className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="label-eyebrow">Date</Label><div className="mt-1.5 font-mono text-sm">{today.date}</div></div>
              <div><Label className="label-eyebrow">CS No.</Label><div className="mt-1.5 font-mono text-sm text-muted">auto-generated</div></div>
            </div>
            <div><Label className="label-eyebrow">Amount (₹)</Label>
              <Input type="number" min="0.01" step="0.01" required value={cs.amount} onChange={(e) => setCS({ ...cs, amount: e.target.value })}
                data-testid="cs-amount" className="mt-1.5 font-mono" /></div>
            <div><Label className="label-eyebrow">Category (required)</Label>
              <Select value={cs.category_id} onValueChange={(v) => setCS({ ...cs, category_id: v })}>
                <SelectTrigger className="mt-1.5" data-testid="cs-category"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>{categories.filter(c => c.type === "in" || c.type === "both").map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
              </Select></div>
            <div><Label className="label-eyebrow">Particulars</Label>
              <Input required value={cs.particulars} onChange={(e) => setCS({ ...cs, particulars: e.target.value })} data-testid="cs-particulars" className="mt-1.5" /></div>
            <div><Label className="label-eyebrow">Remarks</Label>
              <Textarea rows={2} value={cs.remarks} onChange={(e) => setCS({ ...cs, remarks: e.target.value })} className="mt-1.5" /></div>
            <DialogFooter><Button type="button" variant="outline" onClick={() => setOpenCS(false)}>Cancel</Button>
              <Button type="submit" disabled={submitting} data-testid="cs-submit-btn" className="bg-cashin hover:bg-cashin/90 text-white">Save CS</Button></DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Cash Voucher */}
      <Dialog open={openCV} onOpenChange={setOpenCV}>
        <DialogContent>
          <DialogHeader><DialogTitle className="flex items-center gap-2"><FileText size={18} /> Cash Voucher / CV</DialogTitle>
            <DialogDescription>Cash paid out against a direct expense (bill available).</DialogDescription></DialogHeader>
          <form onSubmit={submitCV} className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="label-eyebrow">Date</Label><div className="mt-1.5 font-mono text-sm">{today.date}</div></div>
              <div><Label className="label-eyebrow">CV No.</Label><div className="mt-1.5 font-mono text-sm text-muted">auto-generated</div></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="label-eyebrow">Amount (₹)</Label>
                <Input type="number" min="0.01" step="0.01" required value={cv.amount} onChange={(e) => setCV({ ...cv, amount: e.target.value })}
                  data-testid="cv-amount" className="mt-1.5 font-mono" /></div>
              <div><Label className="label-eyebrow">Category (required)</Label>
                <Select value={cv.category_id} onValueChange={(v) => setCV({ ...cv, category_id: v })}>
                  <SelectTrigger className="mt-1.5" data-testid="cv-category"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>{categories.filter(c => c.type === "out" || c.type === "both").map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                </Select></div>
            </div>
            <div><Label className="label-eyebrow">Person (optional)</Label>
              <Select value={cv.person_id || "_none"} onValueChange={(v) => setCV({ ...cv, person_id: v === "_none" ? "" : v })}>
                <SelectTrigger className="mt-1.5" data-testid="cv-person"><SelectValue placeholder="None" /></SelectTrigger>
                <SelectContent><SelectItem value="_none">None</SelectItem>{persons.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
              </Select></div>
            <div><Label className="label-eyebrow">Particulars</Label>
              <Input required value={cv.particulars} onChange={(e) => setCV({ ...cv, particulars: e.target.value })} data-testid="cv-particulars" className="mt-1.5" /></div>
            <div><Label className="label-eyebrow">Remarks</Label>
              <Textarea rows={2} value={cv.remarks} onChange={(e) => setCV({ ...cv, remarks: e.target.value })} className="mt-1.5" /></div>
            <DialogFooter><Button type="button" variant="outline" onClick={() => setOpenCV(false)}>Cancel</Button>
              <Button type="submit" disabled={submitting} data-testid="cv-submit-btn" className="bg-cashout hover:bg-cashout/90 text-white">Save CV</Button></DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <AttachmentManager
        open={attach.open}
        txnId={attach.txnId}
        docNo={attach.docNo}
        onOpenChange={(o) => setAttach((s) => ({ ...s, open: o }))}
        onChanged={load}
      />
    </div>
  );
}

function StatCard({ label, v, accent, strong, warn, warnLow, testid }) {
  const c = accent === "in" ? "text-cashin" : accent === "out" ? "text-cashout" : warn ? "text-warn" : "text-ink";
  // A money figure must never truncate and never break mid-number: whitespace-nowrap keeps it on one
  // line, and the type scales down (rather than the old break-words, which is what turned a narrow card
  // into a column of digits) so the whole figure still fits.
  return (
    <Card className="p-4 border border-line bg-paper-card overflow-hidden" data-testid={testid}>
      <div className="label-eyebrow">{label}</div>
      <div className={`mt-1.5 font-mono tabular-nums whitespace-nowrap ${strong ? "text-base sm:text-lg lg:text-2xl font-bold" : "text-sm sm:text-base lg:text-lg font-semibold"} ${c}`}>{inr(v)}</div>
      {warnLow && <div className="mt-1 text-xs text-warn flex items-center gap-1"><AlertTriangle size={11} /> Low</div>}
    </Card>
  );
}
